import { useModal } from 'modals';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import useAppletsUrl from '../../hooks/useAppletsUrl';
import useHasHydrated from '../../hooks/useHasHydrated';
import LogoSlider, { type LogoSliderItem } from '../LogoSlider';
import MarketingMedia from '../MarketingMedia';
import NavigationLink from '../NavigationLink';
import StartOrLaunchButton from '../StartOrLaunchButton';
import { firstContentSpacing } from '../first-content-spacing';
import { pageFrame } from '../page-frame';
import { publicPageGutters } from '../public-page-gutters';
import DeferredResearchGraph from './DeferredResearchGraph';
// Not in the current Trusted by design:
// import blizzardLogo from './trusted-by-logos/blizzard.svg?no-inline';
// import disneyLogo from './trusted-by-logos/disney.svg?no-inline';
// import eaLogo from './trusted-by-logos/ea.svg?no-inline';
// import epicGamesLogo from './trusted-by-logos/epic-games.svg?no-inline';
// import metaLogo from './trusted-by-logos/meta.svg?no-inline';
import {
  type HomepageAction,
  type HomepageCardContent,
  type HomepageMedia,
  domainCards,
  heroMedia,
  modelProducts,
  models,
  pipelineProducts,
} from './content';
import { chooseHomepageHeroStart } from './hero-start';
import bitReactorLogo from './trusted-by-logos/bit-reactor.svg?no-inline';
import bioWareLogo from './trusted-by-logos/bioware.svg?no-inline';
import dogstarLogo from './trusted-by-logos/dogstar.svg?no-inline';
import frameMachineLogo from './trusted-by-logos/frame-machine.svg?no-inline';
import googleLogo from './trusted-by-logos/google.svg?no-inline';
import locksmithAnimationLogo from './trusted-by-logos/locksmith-animation.svg?no-inline';
import mrXLogo from './trusted-by-logos/mr-x.svg?no-inline';
import psyopLogo from './trusted-by-logos/psyop.svg?no-inline';
import snapIncLogo from './trusted-by-logos/snap-inc.svg?no-inline';
import theThirdFloorLogo from './trusted-by-logos/the-third-floor.svg?no-inline';

const cardHeading = { base: '24', '2xl': '28' } as const;
const cardDescription = 'text-secondary tracking-[-0.02em] text-pretty';
const researchBody = { base: '16', sm: '20', '2xl': '24' } as const;

interface CardActionsProps {
  actions: HomepageAction[];
  surface?: 'base' | 'alternative';
}

function CardActions({ actions, surface = 'base' }: CardActionsProps) {
  const { open } = useModal();
  const { authStatus } = useSnapshot(authStore.state);
  const appletsUrl = useAppletsUrl();
  const hasHydrated = useHasHydrated();
  const isSignedIn = hasHydrated && authStatus === 'user';

  return (
    <div className="flex flex-wrap items-center gap-2" role="group">
      {actions.map((action) => {
        if (action.type === 'contact') {
          return (
            <Button
              key={action.label}
              type="button"
              variant={action.variant}
              surface={surface}
              onClick={() => open('ENTERPRISE_CONTACT', {})}
            >
              {action.label}
            </Button>
          );
        }
        if (action.type === 'unavailable') {
          return (
            <Button
              key={action.label}
              type="button"
              variant={action.variant}
              surface={surface}
              disabled
              title={action.reason}
              aria-label={`${action.label}: ${action.reason}`}
            >
              {action.label}
            </Button>
          );
        }
        if (isSignedIn && action.signedInAppletPath && !appletsUrl) {
          return (
            <Button
              key={action.label}
              type="button"
              variant={action.variant}
              surface={surface}
              disabled
              aria-busy="true"
            >
              {action.label}
            </Button>
          );
        }
        const signedInHref =
          isSignedIn && action.signedInAppletPath
            ? `${appletsUrl}${action.signedInAppletPath}`
            : isSignedIn
              ? action.signedInHref
              : undefined;
        const href = signedInHref ?? action.href;
        return (
          <Button
            as={NavigationLink}
            key={action.label}
            href={href}
            aria-label={action.ariaLabel}
            external={signedInHref ? href.startsWith('http') : action.external}
            variant={action.variant}
            surface={surface}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}

interface CardMediaProps {
  media: HomepageMedia;
}

function CardMedia({ media }: CardMediaProps) {
  return (
    <MarketingMedia
      media={media}
      retainAfterLoaded={false}
      className="mt-auto aspect-video shrink-0 rounded-lg"
    />
  );
}

function SectionHeader({
  eyebrow,
  title,
  narrow = false,
  research = false,
}: {
  eyebrow: string;
  title: string;
  narrow?: boolean;
  research?: boolean;
}) {
  return (
    <header className={`flex flex-col ${research ? 'gap-10' : 'gap-8'}`}>
      <Text
        as="p"
        token={{ base: '24/medium', lg: '32/medium' }}
        leading="editorial"
      >
        {eyebrow}
      </Text>
      <Text
        as="h2"
        token={research ? { base: '24', '2xl': '28' } : '24'}
        leading="editorial"
        className={`text-secondary text-pretty ${
          narrow ? 'max-w-[648px]' : 'max-w-[813px]'
        }`}
      >
        {title}
      </Text>
    </header>
  );
}

function ModelCard({ card }: { card: HomepageCardContent }) {
  return (
    <article className="bg-secondary flex min-w-0 flex-col gap-8 rounded-2xl p-6 xl:gap-10 xl:p-8 2xl:p-12">
      <div className="flex flex-1 flex-col gap-6">
        <Text
          as="h3"
          token={cardHeading}
          leading="editorial"
          className="text-pretty 2xl:tracking-[-0.02em]"
        >
          {card.title}
        </Text>
        <Text
          as="p"
          token="16"
          leading="editorial"
          className={`${cardDescription} w-full xl:max-w-[444px] 2xl:max-w-[452px]`}
        >
          {card.description}
        </Text>
        <CardActions actions={card.actions} surface="alternative" />
      </div>
      <CardMedia media={card.media} />
    </article>
  );
}

function ProductCard({ card }: { card: HomepageCardContent }) {
  return (
    <article className="bg-secondary flex min-w-0 flex-col justify-between gap-8 rounded-2xl p-6 xl:gap-10 xl:p-8 2xl:p-12">
      <div className="flex flex-col gap-6">
        <div className="flex h-[22px] items-center gap-4 max-[355px]:h-auto max-[355px]:flex-wrap">
          <Text
            as="h4"
            token={cardHeading}
            leading="editorial"
            className="text-pretty 2xl:tracking-[-0.02em]"
          >
            {card.title}
          </Text>
          {card.badge && (
            <Text
              as="span"
              token="14/medium"
              leading="compactEditorial"
              className="text-gray-70 flex h-[34px] w-[54px] shrink-0 items-center justify-center rounded-lg bg-white"
            >
              {card.badge}
            </Text>
          )}
        </div>
        <Text
          as="p"
          token="16"
          leading="editorial"
          className={`${cardDescription} w-full min-[1360px]:max-w-[275px] 2xl:max-w-[341px]`}
        >
          {card.description}
        </Text>
        {card.actions.length > 0 && (
          <CardActions actions={card.actions} surface="alternative" />
        )}
      </div>
      <CardMedia media={card.media} />
    </article>
  );
}

function Hero() {
  const { open } = useModal();
  const [heroStart, setHeroStart] = useState<number | null>(null);
  const selectedHeroStart = useRef<number | null>(null);
  useEffect(() => {
    if (selectedHeroStart.current !== null) return;
    selectedHeroStart.current = chooseHomepageHeroStart(
      window.location.search,
      document,
      window.location.protocol,
    );
    setHeroStart(selectedHeroStart.current);
  }, []);
  return (
    <section
      className={`border-primary border-b pb-20 ${firstContentSpacing} ${publicPageGutters}`}
    >
      <div className="flex flex-col gap-20 xl:gap-[62px]">
        <div className="flex max-w-[813px] flex-col gap-8">
          <Text
            as="h1"
            token="24"
            leading="editorial"
            className="text-secondary max-w-[813px] text-pretty"
          >
            Cartwheel is an{' '}
            <span className="text-primary cw-green-underline">
              AI research lab
            </span>{' '}
            and{' '}
            <span className="text-primary cw-green-underline">
              product company
            </span>
            . We build foundation models for human and object motion, and the
            tools that put them to work across media and the physical world.
          </Text>
          <div className="flex flex-wrap gap-2 2xl:shrink-0">
            <Button
              type="button"
              variant="secondary"
              onClick={() => open('ENTERPRISE_CONTACT', {})}
            >
              Discuss a project
            </Button>
            <StartOrLaunchButton hideWhenLoggedIn />
          </div>
        </div>
        <div className="flex flex-col gap-10">
          <MarketingMedia
            media={heroMedia}
            eagerPoster
            startTime={heroStart}
            retainAfterLoaded={false}
            className="mx-auto aspect-video w-full rounded-2xl lg:max-w-[calc(177.77778dvh-128px)]"
          />
          <TrustedBy />
        </div>
      </div>
    </section>
  );
}

const trustedByLogos: LogoSliderItem[] = [
  {
    label: 'Psyop',
    content: (
      <img
        src={psyopLogo}
        alt=""
        width={84.6}
        height={22}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[22px] w-[84.6px]"
      />
    ),
  },
  {
    label: 'Dogstar',
    content: (
      <img
        src={dogstarLogo}
        alt=""
        width={119.6}
        height={80}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-20 w-[119.6px]"
      />
    ),
  },
  {
    label: 'Google',
    content: (
      <img
        src={googleLogo}
        alt=""
        width={116.2}
        height={38}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[38px] w-[116.2px]"
      />
    ),
  },
  {
    label: 'MR X',
    content: (
      <img
        src={mrXLogo}
        alt=""
        width={103}
        height={45}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[45px] w-[103px]"
      />
    ),
  },
  {
    label: 'Bit Reactor',
    content: (
      <img
        src={bitReactorLogo}
        alt=""
        width={150}
        height={34}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[34px] w-[150px]"
      />
    ),
  },
  {
    label: 'Locksmith Animation',
    content: (
      <img
        src={locksmithAnimationLogo}
        alt=""
        width={143}
        height={44}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[44px] w-[143px]"
      />
    ),
  },
  {
    label: 'Snap Inc.',
    content: (
      <img
        src={snapIncLogo}
        alt=""
        width={153.7}
        height={38}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[38px] w-[153.7px]"
      />
    ),
  },
  {
    label: 'FrameMachine',
    content: (
      <img
        src={frameMachineLogo}
        alt=""
        width={88}
        height={40}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[40px] w-[88px]"
      />
    ),
  },
  {
    label: 'The Third Floor',
    content: (
      <img
        src={theThirdFloorLogo}
        alt=""
        width={230}
        height={37.1551}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[37.1551px] w-[230px]"
      />
    ),
  },
  {
    label: 'BioWare',
    content: (
      <img
        src={bioWareLogo}
        alt=""
        width={150}
        height={26.501}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-[26.501px] w-[150px]"
      />
    ),
  },
  // { label: 'Epic Games', content: <img src={epicGamesLogo} alt="" width={54.2} height={80} loading="lazy" decoding="async" className="h-20 w-[54.2px]" /> },
  // { label: 'Disney', content: <img src={disneyLogo} alt="" width={124} height={80} loading="lazy" decoding="async" className="h-20 w-[124px]" /> },
  // { label: 'Blizzard', content: <img src={blizzardLogo} alt="" width={136.5} height={80} loading="lazy" decoding="async" className="h-20 w-[136.5px]" /> },
  // { label: 'Meta', content: <img src={metaLogo} alt="" width={143.9} height={80} loading="lazy" decoding="async" className="h-20 w-[143.9px]" /> },
  // { label: 'EA', content: <img src={eaLogo} alt="" width={172} height={80} loading="lazy" decoding="async" className="h-20 w-[172px]" /> },
];

function TrustedBy() {
  return (
    <LogoSlider
      ariaLabel="Trusted by"
      heading={
        <Text
          as="p"
          token="12/medium"
          leading="editorial"
          className="tracking-[0.08em]"
        >
          TRUSTED BY
        </Text>
      }
      logos={trustedByLogos}
    />
  );
}

function Products() {
  return (
    <section className={`border-primary border-b py-20 ${publicPageGutters}`}>
      <div className="flex flex-col gap-20">
        <SectionHeader
          eyebrow="Products"
          title="Capture, create and craft motion with precision and scale."
        />
        <ProductGroup title="Apps" cards={modelProducts} />
        <ProductGroup title="Pipeline Tools" cards={pipelineProducts} />
      </div>
    </section>
  );
}

function ProductGroup({
  cards,
  title,
}: {
  cards: HomepageCardContent[];
  title: string;
}) {
  return (
    <section
      className="flex flex-col gap-10"
      aria-labelledby={title.replaceAll(' ', '-')}
    >
      <Text
        id={title.replaceAll(' ', '-')}
        as="h3"
        token={{ base: '20', lg: '24', '2xl': '28' }}
        leading={{
          base: 'editorial',
          lg: 'headline',
          '2xl': 'compactEditorial',
        }}
        className="text-gray-65"
      >
        {title}
      </Text>
      <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10 min-[1360px]:grid-cols-3">
        {cards.map((card) => (
          <ProductCard key={card.id} card={card} />
        ))}
      </div>
    </section>
  );
}

function Domains() {
  const { open } = useModal();
  return (
    <section className={`border-primary border-b py-20 ${publicPageGutters}`}>
      <div className="flex flex-col gap-20">
        <SectionHeader
          eyebrow="Domains"
          title="Perception, analysis, and creation across physical and imagined worlds"
          narrow
        />
        <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10">
          {domainCards.map((card) => (
            <article
              className="bg-secondary flex min-w-0 flex-col justify-between gap-8 rounded-2xl p-6 xl:gap-10 xl:p-8 2xl:p-12"
              key={card.id}
            >
              <div className="flex flex-1 flex-col gap-6">
                <Text
                  as="h3"
                  token={cardHeading}
                  leading="editorial"
                  className="text-pretty 2xl:tracking-[-0.02em]"
                >
                  {card.title}
                </Text>
                <Text
                  as="p"
                  token="16"
                  leading="editorial"
                  className={`${cardDescription} w-full xl:max-w-[444px] 2xl:max-w-[452px]`}
                >
                  {card.description}
                </Text>
              </div>
              <CardMedia media={card.media} />
            </article>
          ))}
        </div>
        <article className="bg-secondary flex flex-col gap-6 rounded-2xl p-6 xl:p-8 2xl:p-12">
          <Text
            as="h3"
            token={cardHeading}
            leading="editorial"
            className="text-pretty 2xl:tracking-[-0.02em]"
          >
            Custom motion models
          </Text>
          <Text
            as="p"
            token="16"
            leading="editorial"
            className={`${cardDescription} max-w-[784px]`}
          >
            Work with us to build your custom models for analysis, style,
            character consistency and more
          </Text>
          <Button
            type="button"
            variant="cta"
            className="w-fit"
            onClick={() => open('ENTERPRISE_CONTACT', {})}
          >
            Discuss a project
          </Button>
        </article>
      </div>
    </section>
  );
}

function Research() {
  return (
    <section
      className={`border-primary border-b border-t py-20 ${publicPageGutters}`}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-10">
          <SectionHeader
            eyebrow="Research"
            title="Model motion, model the world."
            research
          />
          <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10 2xl:grid-cols-[648px_minmax(0,1fr)]">
            <Text
              as="p"
              token={researchBody}
              leading="editorial"
              className="text-gray-65"
            >
              The AI systems that will operate within the physical world —
              humanoid robots, world models, autonomous agents — need training
              data that reflects that reality.
            </Text>
            <Text
              as="p"
              token={researchBody}
              leading="editorial"
              className="text-gray-65"
            >
              At Cartwheel, we build the{' '}
              <span className="text-primary cw-green-underline">
                kinetic world models and datasets that make physical AI training
                tractable at scale
              </span>
              .
            </Text>
          </div>
        </div>
        <Text
          as="h2"
          token={{
            base: 'display/32/bold',
            sm: 'display/40/bold',
            lg: 'display/56/bold',
            xl: 'display/64/bold',
            '2xl': 'display/88/bold',
          }}
          leading="editorial"
          className="pb-10 pt-6 lg:pt-10"
        >
          The <span className="text-green">human body</span> is the world&apos;s
          most powerful platform and the{' '}
          <span className="text-green">fastest route to physical AI</span>.
        </Text>
        <div className="grid gap-20 xl:grid-cols-[349px_minmax(0,1fr)] xl:items-start xl:gap-10 2xl:grid-cols-[480px_minmax(0,1fr)] 2xl:items-center">
          <div className="flex flex-col gap-10 2xl:gap-[55px]">
            <Text
              as="p"
              token={researchBody}
              leading="editorial"
              className="text-gray-65"
            >
              We&apos;ve discovered that{' '}
              <span className="text-primary cw-green-underline">
                human motion data scales.
              </span>{' '}
              Models that generate human kinematic spatiotemporal data improve
              at a predictable rate with increased training data, compute, model
              size and the correct training approach.
              <br />
              <br />
              <span className="text-primary cw-green-underline">
                All rapidly scaling data modalities did so after a similar
                pattern was discovered
              </span>
              : language, code, image, audio, video, self-driving. Human
              kinematics scale in the same regime as language, with 300x more
              efficiency.
              <br />
              <br />
              We&apos;ve built and trained on{' '}
              <span className="text-primary cw-green-underline">
                the largest human motion corpus
              </span>{' '}
              assembled to date.
            </Text>
            <Button
              as={NavigationLink}
              href="/research"
              variant="cta"
              className="w-fit"
            >
              Read the research
            </Button>
          </div>
          <DeferredResearchGraph />
        </div>
      </div>
    </section>
  );
}

function ClosingCtas() {
  return (
    <section className={`border-primary border-b py-20 ${publicPageGutters}`}>
      <div className="flex flex-col gap-20">
        <Text
          as="h2"
          token={{ base: '24/medium', lg: '32/medium' }}
          leading="editorial"
          className="text-pretty"
        >
          If you&apos;re building embodied AI, we&apos;d like to talk.
        </Text>
        <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10">
          <ClosingCard
            title="Try our product suite"
            description="Capture motion, generate in batches, retarget and more"
          />
          <ClosingCard
            title="Build for custom needs"
            description="Custom datasets, models, and evals"
            contact
          />
        </div>
      </div>
    </section>
  );
}

function ClosingCard({
  contact = false,
  description,
  title,
}: {
  contact?: boolean;
  description: string;
  title: string;
}) {
  const { open } = useModal();
  return (
    <article className="bg-secondary flex min-h-[271px] flex-col gap-6 rounded-2xl p-6 xl:p-8 2xl:p-12">
      <Text
        as="h3"
        token={cardHeading}
        leading="editorial"
        className="text-pretty 2xl:tracking-[-0.02em]"
      >
        {title}
      </Text>
      <Text
        as="p"
        token="16"
        leading="editorial"
        className={`${cardDescription} w-full xl:max-w-[444px] 2xl:max-w-[452px]`}
      >
        {description}
      </Text>
      {contact ? (
        <Button
          type="button"
          variant="cta"
          className="w-fit"
          onClick={() => open('ENTERPRISE_CONTACT', {})}
        >
          Discuss a project
        </Button>
      ) : (
        <StartOrLaunchButton className="w-fit" />
      )}
    </article>
  );
}

export default function HomePage() {
  return (
    <div className={pageFrame}>
      <Hero />
      <section className={`border-primary border-b py-20 ${publicPageGutters}`}>
        <div className="flex flex-col gap-20">
          <SectionHeader
            eyebrow="Our models"
            title="Frontier models for the perception, capture and generation of motion, physics and kinematic spatiotemporal data."
            narrow
          />
          <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10">
            {models.map((card) => (
              <ModelCard key={card.id} card={card} />
            ))}
          </div>
        </div>
      </section>
      <Products />
      {/* Domains was temporarily removed on Sept 24, 2026 ahead of launch */}
      {/* <Domains /> */}
      <Research />
      <ClosingCtas />
    </div>
  );
}
